import type { RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import clsx from 'clsx'
import type { Heading } from 'datocms-structured-text-utils'
import Icon from './svg/icon'
import useClickOutside from '@/hooks/useClickOutside'

type Prop = {
	headings: Heading[]
	refs: Record<string, RefObject<HTMLHeadingElement>>
}

const isHtmlElement = (ele: Element): ele is HTMLElement => !!('offsetTop' in ele && ele.offsetTop)

export function TableOfContents({ headings, refs }: Prop) {
	const [selectedItem, setSelectedItem] = useState<string | undefined>()
	const [isScrolling, setIsScrolling] = useState(false)
	const tocRef = useRef<HTMLUListElement>(null)
	const [isMobileTOCOpen, setIsMobileTOCOpen] = useState(false)
	const mobileTOCRef = useRef<HTMLDivElement>(null)

	const handleClick = useCallback(
		(id: string | undefined) => {
			if (!headings || !id) return

			const element = refs[id]?.current
			if (element) {
				const currentScroll = element.getBoundingClientRect().top
				const extraScroll = currentScroll - 100
				window.scrollTo({
					behavior: 'smooth',
					top: window.scrollY + extraScroll,
				})
				setIsScrolling(true)
				setSelectedItem(id)
				setIsMobileTOCOpen(false)
			}
		},
		[headings, refs]
	)

	const handleMobileClick = () => {
		setIsMobileTOCOpen(!isMobileTOCOpen)
	}

	useClickOutside(mobileTOCRef, () => {
		setIsMobileTOCOpen(false)
	})

	// Change the selected section while user scrolls through the page
	useEffect(() => {
		if (!refs || !headings?.length) return

		const handleScroll = () => {
			const visibleHeading = headings.find((heading) => {
				if (!heading.children.length || !('value' in heading.children[0])) return

				const id = heading.children.map((child) => child.type === 'span' && child.value).join('')
				const ref = refs[id]

				if (ref?.current) {
					const rect = ref.current.getBoundingClientRect()
					return rect.top <= 125 && rect.bottom >= 125
				}

				return false
			})

			if (visibleHeading && !isScrolling) {
				if (!visibleHeading.children.length || !('value' in visibleHeading.children[0])) {
					return
				}

				setSelectedItem(
					visibleHeading.children.map((child) => child.type === 'span' && child.value).join('')
				)

				if (tocRef.current === null) return

				// Find element in the list that matches the selected item
				const liElement = tocRef.current.querySelector(
					`[data-testid="table-of-content-item"]:nth-child(${headings.indexOf(visibleHeading) + 1})`
				)

				if (liElement && isHtmlElement(liElement)) {
					// if liElement is not visible inside the ul, scroll to it
					if (
						liElement.offsetTop < tocRef.current.scrollTop ||
						liElement.offsetTop > tocRef.current.scrollTop + tocRef.current.offsetHeight
					) {
						tocRef.current.scrollTo({
							behavior: 'smooth',
							top: liElement.offsetTop - liElement.offsetHeight,
						})
					}
				}
			}
		}

		const handleScrollEnd = () => {
			setIsScrolling(false)
		}

		document.addEventListener('scroll', handleScroll)
		document.addEventListener('scrollend', handleScrollEnd)

		handleScroll()

		return () => {
			document.removeEventListener('scroll', handleScroll)
			document.removeEventListener('scrollend', handleScrollEnd)
		}
	}, [refs, isScrolling, selectedItem, headings])

	const menuOptions = useMemo(() => {
		if (!refs || !headings?.length) return null

		return headings.map((heading) => {
			const id =
				heading.children.length && heading.children[0] && 'value' in heading.children[0]
					? heading.children
							.map((child) => (child.type === 'span' ? child.value : null))
							.filter((value) => value !== null)
							.join('')
					: 'value' in heading.children[0]
						? heading.children[0]?.value
						: undefined

			const isSelected = String(selectedItem) === String(id)

			// Section Heading
			return (
				<li
					data-testid="table-of-content-item"
					key={id}
					className={clsx(
						'relative cursor-pointer md:pl-4',
						'before:content-[""] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-2 before:w-2 before:rounded-full before:max-md:hidden max-md:list-none',
						isSelected ? 'text-contrast before:bg-blue' : 'md:text-contrast/50 before:bg-contrast'
					)}
					onClick={() => handleClick(id)}
				>
					{id}
				</li>
			)
		})
	}, [handleClick, headings, refs, selectedItem])

	if (!refs || !headings.length) return null

	const hasOptions = menuOptions && menuOptions.length > 0

	return (
		hasOptions && (
			<>
				{/* Desktop TOC */}
				<div
					data-testid="table-of-contents"
					className="max-md:hidden flex flex-col gap-6 mb-6 overflow-y-auto"
				>
					<span className="relative text-2xl font-medium tracking-tight pl-3 before:content-[''] before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-blue">
						Contents
					</span>
					<hr />
					<ul
						ref={tocRef}
						className="relative flex flex-col gap-2 overflow-y-auto before:content-[''] before:absolute before:inset-y-2 before:left-[3.5px] before:w-px before:bg-contrast-low/30"
					>
						{menuOptions}
					</ul>
				</div>
				{/* Mobile TOC */}
				<div ref={mobileTOCRef} className="md:hidden p-3">
					<button
						onClick={(e) => {
							e.preventDefault()
							handleMobileClick()
						}}
						className={clsx(
							'flex items-center justify-center gap-2 bg-white-frosted w-full py-6 ',
							isMobileTOCOpen ? 'rounded-t-lg' : 'rounded-lg'
						)}
					>
						<span className="text-xs uppercase tracking-wider">
							{selectedItem ?? menuOptions[0]}
						</span>
						<Icon
							icon="ChevronBold"
							className={clsx(
								'w-3.5 text-blue transition-transform duration-300',
								isMobileTOCOpen ? '-rotate-90' : 'rotate-90'
							)}
						/>
					</button>

					<div
						className={clsx(
							'grid absolute inset-x-3 py-5 overflow-hidden border border-t-0 transition-all duration-300 rounded-b-lg',
							isMobileTOCOpen
								? 'grid-rows-[1fr]  bg-white border-contrast-low/30'
								: 'grid-rows-[0fr] border-transparent'
						)}
					>
						<ul
							ref={tocRef}
							className={clsx(
								'relative flex flex-col gap-2.5 overflow-y-auto overflow-hidden text-center'
							)}
						>
							{menuOptions}
						</ul>
					</div>
				</div>
			</>
		)
	)
}
